/** Messagerie : 1:1 temps réel, accusés, photos, éphémères, édition/suppression, caméra. */
import { el, clear, escapeHtml } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { api } from '../api.js';
import { toast } from '../ui/toast.js';
import { promptModal, confirmModal, openModal, contextMenu } from '../ui/modal.js';
import { requestAndSetupPush } from '../push.js';

const time = (ts) => new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const dayKey = (ts) => new Date(ts).toDateString();
const sv = (p, w = 20, h = 20) => `<svg viewBox="0 0 24 24" width="${w}" height="${h}" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`;
const PATH = {
  send: '<path d="M5 12 19 5l-4 14-3.5-6.5L5 12Z"/>',
  image: '<rect x="3" y="4" width="18" height="16" rx="2.5"/><circle cx="8.5" cy="9.5" r="1.8"/><path d="m4 17 5-4 4 3 3-3 4 3"/>',
  flame: '<path d="M12 3c.6 3-2 4.2-2 7a2 2 0 0 0 4 0c0-.8-.2-1.3.3-2.2C16 9.4 17 11 17 13a5 5 0 1 1-10 0c0-4 3-6.2 5-10Z"/>',
  flip: '<path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5"/>',
};

export function renderMessages(ctx) {
  const { content, user } = ctx;
  ctx.setCrumbs([{ label: 'Messages' }]);

  let convos = [], activeId = null, msgs = [], other = null, fileMode = 'photo', firstLoad = true, lastTyping = 0, typingTimer = null;

  function sendTyping() { const n = Date.now(); if (n - lastTyping > 1800 && activeId) { lastTyping = n; socket.send({ t: 'typing', conversationId: activeId }); } }
  function showTyping() {
    if (!threadPane._sub) return;
    threadPane._sub.textContent = 'en train d’écrire…'; threadPane._sub.classList.add('typing');
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => { if (threadPane._sub) { threadPane._sub.textContent = lastSeenText(other); threadPane._sub.classList.remove('typing'); } }, 3000);
  }
  const socket = ctx.shell.getChat();
  const unsub = [];

  // Demande l'autorisation des notifications + abonnement push (geste = ouverture de Messages).
  requestAndSetupPush();

  const listPane = el('div', { class: 'msg-list' });
  const threadPane = el('div', { class: 'msg-thread' });
  const root = el('div', { class: 'messenger' }, listPane, threadPane);
  content.style.padding = '0';
  content.appendChild(root);

  buildListPane();
  showThreadPlaceholder();
  loadConvos();
  connectSocket();

  // ───────── Conversations ─────────
  function buildListPane() {
    const head = el('div', { class: 'msg-list-head' }, el('h2', {}, 'Messages'),
      el('button', { class: 'btn btn-icon btn-primary', title: 'Nouvelle conversation', html: icon('plus', 18), onclick: newConversation }));
    const items = el('div', { class: 'msg-conv-items' });
    listPane.append(head, items);
    listPane._items = items;
  }
  async function loadConvos() {
    try { ({ conversations: convos } = await api.get('/api/chat/conversations')); } catch (e) { return toast(e.message, 'err'); }
    renderConvList();
    // 1er affichage : un re-rendu après peinture pour que les photos de contacts apparaissent.
    if (firstLoad) { firstLoad = false; requestAnimationFrame(() => requestAnimationFrame(() => renderConvList())); }
    // Ouverture directe d'une conversation (ex. depuis le panneau admin).
    const auto = sessionStorage.getItem('jhn-open-conv');
    if (auto) { sessionStorage.removeItem('jhn-open-conv'); if (convos.find((c) => c.id === auto)) openConv(auto); }
  }
  function renderConvList() {
    const items = listPane._items; clear(items);
    if (!convos.length) { items.appendChild(el('div', { class: 'empty', style: { padding: '40px 16px' } }, el('div', { class: 'ico', html: icon('chat', 28) }), el('h3', {}, 'Aucune conversation'), el('div', {}, 'Touchez + pour démarrer.'))); return; }
    for (const c of convos) {
      const o = c.other || {};
      const name = `${o.given_name || ''} ${o.family_name || ''}`.trim() || o.nickname || o.email || 'Inconnu';
      const item = el('div', { class: 'msg-conv' + (c.id === activeId ? ' active' : '') }, avatarStatus(o, name, 46),
        el('div', { class: 'msg-conv-main' },
          el('div', { class: 'msg-conv-top' }, el('b', {}, name), el('span', { class: 'msg-conv-time' }, c.last_message_at ? time(c.last_message_at) : '')),
          el('div', { class: 'msg-conv-bot' }, el('span', { class: 'msg-conv-prev' }, c.last_message ? previewText(c.last_message) : ''), c.unread ? el('span', { class: 'msg-badge' }, String(c.unread)) : '')));
      item.addEventListener('click', () => openConv(c.id));
      items.appendChild(item);
    }
  }
  function previewText(lm) {
    const me = lm.sender_id === user.id ? 'Vous : ' : '';
    if (lm.type === 'photo') return me + (lm.ephemeral ? '📸 Photo éphémère' : '🖼️ Photo');
    return me + (lm.body || '');
  }
  async function newConversation() {
    const id = await promptModal({ title: 'Nouvelle conversation', label: 'Email, Rayor ID ou pseudo', placeholder: 'ex. camille@…', confirm: 'Démarrer' });
    if (!id) return;
    try { const { conversation } = await api.post('/api/chat/conversations', { identifier: id }); if (!convos.find((c) => c.id === conversation.id)) convos.unshift(conversation); renderConvList(); openConv(conversation.id); }
    catch (e) { toast(e.message, 'err'); }
  }

  // ───────── Fil ─────────
  function showThreadPlaceholder() {
    clear(threadPane); root.classList.remove('showing-thread'); ctx.shell.setActiveConversation(null); activeId = null; renderConvList();
    threadPane.appendChild(el('div', { class: 'msg-empty-thread' }, el('div', { class: 'ico', html: icon('chat', 34) }), el('h3', {}, 'Vos messages'), el('p', {}, 'Sélectionnez une conversation ou démarrez-en une.')));
  }
  async function openConv(id) {
    activeId = id; const c = convos.find((x) => x.id === id); other = c ? c.other : null; if (c) c.unread = 0;
    ctx.shell.setActiveConversation(id); renderConvList(); root.classList.add('showing-thread');
    // Marque lu côté serveur tout de suite (REST), pour que le badge ne réapparaisse pas.
    api.post(`/api/chat/conversations/${id}/read`).catch(() => {});
    try { ({ messages: msgs } = await api.get(`/api/chat/conversations/${id}/messages`)); } catch (e) { return toast(e.message, 'err'); }
    buildThread(); socket.send({ t: 'read', conversationId: id });
  }
  function buildThread() {
    clear(threadPane);
    const name = other ? (`${other.given_name || ''} ${other.family_name || ''}`.trim() || other.nickname || other.email) : 'Conversation';
    const headAv = avatarStatus(other || {}, name, 38);
    const sub = el('div', { class: 'msg-sub' }, lastSeenText(other));
    const header = el('div', { class: 'msg-thread-head' },
      el('button', { class: 'btn btn-icon btn-ghost msg-back', html: icon('back', 20), onclick: showThreadPlaceholder }),
      headAv, el('div', {}, el('b', {}, name), sub));
    threadPane._sub = sub;
    threadPane._headDot = headAv.querySelector('.avatar-dot');
    const scroll = el('div', { class: 'msg-scroll' });
    const input = el('textarea', { class: 'msg-input', rows: 1, placeholder: 'Message…' });
    const fileInput = el('input', { type: 'file', accept: 'image/*', style: { display: 'none' } });
    fileInput.addEventListener('change', () => { if (fileInput.files[0]) uploadPhoto(fileInput.files[0], fileMode === 'ephemeral'); fileInput.value = ''; });
    const photoBtn = el('button', { class: 'btn btn-icon btn-ghost', title: 'Photo', html: sv(PATH.image), onclick: () => { fileMode = 'photo'; fileInput.removeAttribute('capture'); fileInput.click(); } });
    const snapBtn = el('button', { class: 'btn btn-icon btn-ghost msg-snap', title: 'Photo éphémère (caméra)', html: sv(PATH.flame), onclick: () => openCamera() });
    const sendBtn = el('button', { class: 'btn btn-icon btn-primary', title: 'Envoyer', html: sv(PATH.send) });
    const sendNow = () => { const v = input.value.trim(); if (!v) return; input.value = ''; input.style.height = 'auto'; sendText(v); };
    sendBtn.addEventListener('click', sendNow);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendNow(); } });
    input.addEventListener('input', () => { input.style.height = 'auto'; input.style.height = Math.min(input.scrollHeight, 120) + 'px'; sendTyping(); });
    threadPane.append(header, scroll, el('div', { class: 'msg-composer' }, photoBtn, snapBtn, el('div', { class: 'msg-input-wrap' }, input), sendBtn, fileInput));
    threadPane._scroll = scroll;
    renderThread();
    setTimeout(() => input.focus(), 60);
  }
  function renderThread() {
    const scroll = threadPane._scroll; if (!scroll) return; clear(scroll);
    let lastReadMineId = null;
    for (const m of msgs) if (m.sender_id === user.id && m.read_at && !m.deleted) lastReadMineId = m.id;
    let lastDay = null;
    for (const m of msgs) {
      const dk = dayKey(m.created_at);
      if (dk !== lastDay) { lastDay = dk; scroll.appendChild(el('div', { class: 'msg-day' }, daySep(m.created_at))); }
      scroll.appendChild(bubble(m));
      if (m.id === lastReadMineId) scroll.appendChild(el('div', { class: 'msg-readline' }, 'Lu à ' + time(m.read_at)));
    }
    requestAnimationFrame(() => { scroll.scrollTop = scroll.scrollHeight; });
  }

  const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '🎉'];
  const myReaction = (m) => { const r = (m.reactions || []).find((x) => x.user_id === user.id); return r && r.emoji; };

  async function react(m, emoji) {
    try {
      const { reactions } = await api.post(`/api/chat/conversations/${m.conversation_id}/messages/${m.id}/react`, { emoji });
      m.reactions = reactions; renderThread();
    } catch (e) { toast(e.message, 'err'); }
  }
  function renderReactions(container, m) {
    clear(container);
    const agg = {};
    (m.reactions || []).forEach((r) => { const a = agg[r.emoji] || (agg[r.emoji] = { count: 0, mine: false }); a.count++; if (r.user_id === user.id) a.mine = true; });
    for (const [emoji, a] of Object.entries(agg)) {
      const chip = el('button', { class: 'reaction-chip' + (a.mine ? ' mine' : '') }, emoji + (a.count > 1 ? ' ' + a.count : ''));
      chip.addEventListener('click', () => react(m, emoji));
      container.appendChild(chip);
    }
  }
  function openReactPicker(m, anchor) {
    document.querySelectorAll('.react-pop').forEach((x) => x.remove());
    const pop = el('div', { class: 'react-pop' });
    EMOJIS.forEach((e) => { const btn = el('button', { class: 'react-emoji' + (myReaction(m) === e ? ' on' : '') }, e); btn.addEventListener('click', () => { react(m, e); pop.remove(); }); pop.appendChild(btn); });
    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.left = Math.max(8, Math.min(r.left, innerWidth - pop.offsetWidth - 8)) + 'px';
    pop.style.top = Math.max(8, r.top - pop.offsetHeight - 8) + 'px';
    const dismiss = (ev) => { if (ev.type === 'keydown' && ev.key !== 'Escape') return; if (ev.type === 'mousedown' && pop.contains(ev.target)) return; pop.remove(); document.removeEventListener('mousedown', dismiss, true); document.removeEventListener('keydown', dismiss, true); };
    setTimeout(() => { document.addEventListener('mousedown', dismiss, true); document.addEventListener('keydown', dismiss, true); });
  }
  function actionBar(m) {
    const mine = m.sender_id === user.id;
    const bar = el('div', { class: 'msg-actions' });
    const reactBtn = el('button', { class: 'msg-act', title: 'Réagir', html: '<span style="font-size:15px">😊</span>' });
    reactBtn.addEventListener('click', (e) => { e.stopPropagation(); openReactPicker(m, e.currentTarget); });
    bar.appendChild(reactBtn);
    if (mine && m.type === 'text') { const ed = el('button', { class: 'msg-act', title: 'Modifier', html: icon('rename', 16) }); ed.addEventListener('click', (e) => { e.stopPropagation(); editMessage(m); }); bar.appendChild(ed); }
    if (mine) { const del = el('button', { class: 'msg-act danger', title: 'Supprimer', html: icon('trash', 16) }); del.addEventListener('click', (e) => { e.stopPropagation(); deleteMessage(m); }); bar.appendChild(del); }
    return bar;
  }
  function attachReact(b, m) {
    b.addEventListener('dblclick', (e) => { e.preventDefault(); openReactPicker(m, b); });
    let lastTap = 0;
    b.addEventListener('touchend', () => { const n = Date.now(); if (n - lastTap < 300) openReactPicker(m, b); lastTap = n; }, { passive: true });
  }

  function bubble(m) {
    const mine = m.sender_id === user.id;
    const wrap = el('div', { class: 'msg-wrap ' + (mine ? 'mine' : 'theirs') });
    const b = el('div', { class: 'bubble ' + (mine ? 'mine' : 'theirs') });
    const body = el('div', { class: 'bubble-body' });
    if (m.deleted) {
      b.classList.add('deleted');
      body.appendChild(el('div', { class: 'bubble-text deleted-text' }, '🚫 Message supprimé'));
    } else if (m.type === 'photo') {
      b.classList.add('photo');
      if (m.ephemeral) {
        if (m.opened) body.appendChild(el('div', { class: 'eph opened' }, el('span', { html: sv(PATH.flame, 18) }), mine ? 'Photo éphémère ouverte' : 'Photo ouverte'));
        else if (mine) body.appendChild(el('div', { class: 'eph sent' }, el('span', { html: sv(PATH.flame, 18) }), 'Photo éphémère envoyée'));
        else { const v = el('div', { class: 'eph view' }, el('span', { html: sv(PATH.flame, 18) }), 'Appuyez pour voir'); v.addEventListener('click', () => viewEphemeral(m)); body.appendChild(v); }
      } else {
        b.classList.add('has-img');
        const img = el('img', { class: 'bubble-img', src: `/api/chat/attachment/${m.id}`, alt: 'photo', loading: 'lazy' });
        img.addEventListener('click', () => imageOverlay(`/api/chat/attachment/${m.id}`));
        body.appendChild(img);
        if (m.body) body.appendChild(el('div', { class: 'bubble-cap' }, m.body));
      }
    } else {
      body.appendChild(el('div', { class: 'bubble-text', html: linkify(escapeHtml(m.body)) }));
    }
    const meta = el('div', { class: 'bubble-meta' }, el('span', { class: 'bubble-time' }, time(m.created_at)));
    if (m.edited && !m.deleted) { const ed = el('span', { class: 'bubble-edited' }, 'modifié'); ed.addEventListener('click', (e) => { e.stopPropagation(); showVersions(m); }); meta.appendChild(ed); }
    if (mine && !m.deleted) meta.appendChild(statusEl(m));
    body.appendChild(meta);
    b.appendChild(body);
    wrap.appendChild(b);
    const reChips = el('div', { class: 'reactions' });
    renderReactions(reChips, m);
    wrap.appendChild(reChips);
    if (!m.deleted) { wrap.appendChild(actionBar(m)); attachMenu(b, m); attachReact(b, m); }
    return wrap;
  }

  function statusEl(m) {
    const state = m.read_at ? 'read' : m.delivered_at ? 'delivered' : 'sent';
    const p = '<path d="M4 6l4.5 6L4 18"/>' + (state !== 'sent' ? '<path d="M11 6l4.5 6L11 18"/>' : '');
    return el('span', { class: 'ticks ' + state, html: `<svg viewBox="0 0 20 24" width="22" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">${p}</svg>` });
  }

  // ───────── Menu d'un message (modifier / supprimer / versions) ─────────
  function attachMenu(b, m) {
    b.addEventListener('contextmenu', (e) => { e.preventDefault(); msgMenu(m, e.clientX, e.clientY); });
    let lp;
    b.addEventListener('touchstart', (e) => { const t = e.touches[0]; lp = setTimeout(() => msgMenu(m, t.clientX, t.clientY), 480); }, { passive: true });
    ['touchend', 'touchmove', 'touchcancel'].forEach((ev) => b.addEventListener(ev, () => clearTimeout(lp), { passive: true }));
  }
  function msgMenu(m, x, y) {
    const mine = m.sender_id === user.id, items = [];
    if (m.type === 'text') items.push({ label: 'Copier', icon: 'doc', onClick: () => { try { navigator.clipboard.writeText(m.body); toast('Copié'); } catch {} } });
    if (mine && m.type === 'text') items.push({ label: 'Modifier', icon: 'rename', onClick: () => editMessage(m) });
    if (m.edited) items.push({ label: 'Voir les versions', icon: 'recent', onClick: () => showVersions(m) });
    if (mine) items.push({ sep: true }, { label: 'Supprimer', icon: 'trash', danger: true, onClick: () => deleteMessage(m) });
    if (items.length) contextMenu(x, y, items);
  }
  async function editMessage(m) {
    const v = await promptModal({ title: 'Modifier le message', label: 'Message', value: m.body, confirm: 'Enregistrer' });
    if (v == null || v === m.body) return;
    try { const { message } = await api.patch(`/api/chat/conversations/${m.conversation_id}/messages/${m.id}`, { body: v }); addOrUpdate(message); }
    catch (e) { toast(e.message, 'err'); }
  }
  async function deleteMessage(m) {
    if (!(await confirmModal({ title: 'Supprimer ce message ?', message: 'Il sera supprimé pour tout le monde.', confirm: 'Supprimer', danger: true }))) return;
    try { await api.del(`/api/chat/conversations/${m.conversation_id}/messages/${m.id}`); markDeleted(m.id); }
    catch (e) { toast(e.message, 'err'); }
  }
  async function showVersions(m) {
    try {
      const { versions } = await api.get(`/api/chat/conversations/${m.conversation_id}/messages/${m.id}/versions`);
      openModal((modal, close) => {
        const list = el('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '340px', overflow: 'auto', margin: '12px 0' } });
        versions.slice().reverse().forEach((v) => list.appendChild(el('div', { class: 'ver-item' + (v.current ? ' current' : '') },
          el('div', { class: 'ver-time' }, (v.current ? 'Version actuelle · ' : 'Ancienne version · ') + new Date(v.created_at).toLocaleString('fr-FR')),
          el('div', { class: 'ver-body' }, v.body || '(vide)'))));
        modal.append(el('h3', {}, 'Historique du message'), list, el('div', { class: 'row' }, el('button', { class: 'btn btn-primary', onclick: close }, 'Fermer')));
      });
    } catch (e) { toast(e.message, 'err'); }
  }
  function markDeleted(id) { const m = msgs.find((x) => x.id === id); if (m) { m.deleted = true; m.body = ''; m.edited = false; renderThread(); } }

  // ───────── Envoi ─────────
  async function sendText(body) {
    try { const { message } = await api.post(`/api/chat/conversations/${activeId}/messages`, { body }); addOrUpdate(message); }
    catch (e) { toast(e.message, 'err'); }
  }
  async function uploadPhoto(file, ephemeral) {
    const fd = new FormData();
    fd.append('photo', file, file.name || 'photo.jpg');
    if (ephemeral) fd.append('ephemeral', '1');
    try {
      const res = await fetch(`/api/chat/conversations/${activeId}/photo`, { method: 'POST', body: fd, credentials: 'same-origin' });
      const data = await res.json(); if (!res.ok) throw new Error(data.error || 'Échec de l’envoi');
      addOrUpdate(data.message); toast(ephemeral ? 'Photo éphémère envoyée' : 'Photo envoyée');
    } catch (e) { toast(e.message, 'err'); }
  }
  async function viewEphemeral(m) {
    try {
      const res = await fetch(`/api/chat/attachment/${m.id}`, { credentials: 'same-origin' });
      if (!res.ok) { m.opened = true; renderThread(); return toast('Photo déjà ouverte', 'err'); }
      const url = URL.createObjectURL(await res.blob());
      imageOverlay(url, () => URL.revokeObjectURL(url), true);
      m.opened = true; renderThread();
    } catch { toast('Impossible d’ouvrir la photo', 'err'); }
  }
  function imageOverlay(src, onClose, ephemeral) {
    const ov = el('div', { class: 'img-overlay' + (ephemeral ? ' eph' : '') }, el('img', { src, alt: '' }), el('div', { class: 'img-hint' }, ephemeral ? '📸 Photo éphémère — appuyez pour fermer' : 'Appuyez pour fermer'));
    ov.addEventListener('click', () => { ov.remove(); onClose && onClose(); });
    document.body.appendChild(ov);
  }

  // ───────── Caméra (flamme → photo éphémère) ─────────
  async function openCamera() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // repli : appareil photo natif via input file capture (caméra avant)
      const inp = el('input', { type: 'file', accept: 'image/*', capture: 'user', style: { display: 'none' } });
      inp.addEventListener('change', () => { if (inp.files[0]) uploadPhoto(inp.files[0], true); });
      document.body.appendChild(inp); inp.click(); setTimeout(() => inp.remove(), 60000); return;
    }
    let stream = null, facing = 'user'; // caméra avant par défaut
    const video = el('video', { autoplay: '', playsinline: '', muted: '' }); video.muted = true;
    const shutter = el('button', { class: 'cam-shutter', title: 'Capturer' });
    const flip = el('button', { class: 'cam-btn', title: 'Changer de caméra', html: sv(PATH.flip, 22) });
    const close = el('button', { class: 'cam-btn', title: 'Fermer', html: icon('x', 22) });
    const overlay = el('div', { class: 'cam-overlay' }, video, el('div', { class: 'cam-top' }, close), el('div', { class: 'cam-bar' }, flip, shutter, el('span', { style: { width: '44px' } })));
    document.body.appendChild(overlay);
    const cleanup = () => { if (stream) stream.getTracks().forEach((t) => t.stop()); overlay.remove(); };
    async function start() {
      try {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
        video.srcObject = stream; await video.play().catch(() => {});
        // Aperçu en miroir pour la caméra avant (comme l'app photo native).
        video.style.transform = facing === 'user' ? 'scaleX(-1)' : 'none';
      } catch { toast('Caméra indisponible', 'err'); cleanup(); }
    }
    close.addEventListener('click', cleanup);
    flip.addEventListener('click', () => { facing = facing === 'environment' ? 'user' : 'environment'; start(); });
    shutter.addEventListener('click', () => {
      if (!video.videoWidth) return;
      const c = document.createElement('canvas'); c.width = video.videoWidth; c.height = video.videoHeight;
      const cx = c.getContext('2d');
      if (facing === 'user') { cx.translate(c.width, 0); cx.scale(-1, 1); } // capture cohérente avec l'aperçu miroir
      cx.drawImage(video, 0, 0);
      c.toBlob((blob) => { cleanup(); if (blob) uploadPhoto(blob, true); }, 'image/jpeg', 0.9);
    });
    start();
  }

  // ───────── État local ─────────
  function addOrUpdate(message) {
    const i = msgs.findIndex((x) => x.id === message.id);
    if (i >= 0) msgs[i] = { ...msgs[i], ...message };
    else if (message.conversation_id === activeId) msgs.push(message);
    if (message.conversation_id === activeId) renderThread();
    bumpConvList(message);
  }
  function bumpConvList(message) {
    const c = convos.find((x) => x.id === message.conversation_id);
    if (c) {
      c.last_message = { type: message.type, body: message.body, sender_id: message.sender_id, ephemeral: message.ephemeral, created_at: message.created_at };
      c.last_message_at = message.created_at;
      if (message.conversation_id !== activeId && message.sender_id !== user.id) c.unread = (c.unread || 0) + 1;
      convos.sort((a, b) => b.last_message_at - a.last_message_at); renderConvList();
    } else loadConvos();
  }

  // ───────── Temps réel (socket global de la coquille) ─────────
  function connectSocket() {
    if (activeId) socket.send({ t: 'read', conversationId: activeId });
    unsub.push(socket.on('open', () => { if (activeId) socket.send({ t: 'read', conversationId: activeId }); loadConvos(); }));
    unsub.push(socket.on('message', ({ message }) => {
      addOrUpdate(message);
      if (message.conversation_id === activeId && message.sender_id !== user.id) socket.send({ t: 'read', conversationId: activeId });
    }));
    unsub.push(socket.on('delivered', ({ messageIds, at }) => applyStatus(messageIds, { delivered_at: at })));
    unsub.push(socket.on('read', ({ messageIds, at }) => applyStatus(messageIds, { read_at: at, delivered_at: at })));
    unsub.push(socket.on('opened', ({ messageId }) => { const m = msgs.find((x) => x.id === messageId); if (m) { m.opened = true; renderThread(); } }));
    unsub.push(socket.on('edited', ({ message }) => addOrUpdate(message)));
    unsub.push(socket.on('deleted', ({ messageId, conversationId }) => { if (conversationId === activeId) markDeleted(messageId); }));
    unsub.push(socket.on('reaction', ({ messageId, reactions }) => { const m = msgs.find((x) => x.id === messageId); if (m) { m.reactions = reactions; renderThread(); } }));
    unsub.push(socket.on('typing', ({ conversationId, from }) => { if (conversationId === activeId && from !== user.id) showTyping(); }));
    unsub.push(socket.on('presence', ({ userId, online, last_seen }) => {
      const c = convos.find((x) => x.other && x.other.id === userId);
      if (c) { c.other.online = online; c.other.last_seen = last_seen; renderConvList(); }
      if (other && other.id === userId) {
        other.online = online; other.last_seen = last_seen;
        if (threadPane._sub && !threadPane._sub.classList.contains('typing')) threadPane._sub.textContent = lastSeenText(other);
        if (threadPane._headDot) threadPane._headDot.className = 'avatar-dot ' + (online ? 'online' : 'offline');
      }
    }));
  }
  function applyStatus(ids, patch) { let ch = false; for (const id of ids) { const m = msgs.find((x) => x.id === id); if (m) { Object.assign(m, patch); ch = true; } } if (ch) renderThread(); }

  // ───────── utils ─────────
  function avatarEl(u, name, size) {
    const a = el('div', { class: 'avatar', style: { width: size + 'px', height: size + 'px', fontSize: size * 0.4 + 'px' } });
    if (u && u.picture) a.appendChild(el('img', { src: u.picture, alt: '' })); else a.textContent = (name || '?').trim()[0]?.toUpperCase() || '?';
    return a;
  }
  // Avatar + pastille de statut (verte = en ligne, rouge = hors ligne).
  function avatarStatus(u, name, size) {
    const wrap = el('div', { class: 'avatar-wrap', style: { width: size + 'px', height: size + 'px' } });
    wrap.appendChild(avatarEl(u, name, size));
    wrap.appendChild(el('span', { class: 'avatar-dot ' + (u && u.online ? 'online' : 'offline') }));
    return wrap;
  }
  function lastSeenText(u) {
    if (!u) return '';
    if (u.online) return 'en ligne';
    if (!u.last_seen) return 'hors ligne';
    const s = Math.floor((Date.now() - u.last_seen) / 1000);
    if (s < 60) return 'vu à l’instant';
    if (s < 3600) return `vu il y a ${Math.floor(s / 60)} min`;
    if (s < 86400) return `vu il y a ${Math.floor(s / 3600)} h`;
    const d = Math.floor(s / 86400);
    if (d < 7) return `vu il y a ${d} j`;
    return 'vu le ' + new Date(u.last_seen).toLocaleDateString('fr-FR');
  }
  function daySep(ts) {
    const d = new Date(ts), t = new Date();
    if (d.toDateString() === t.toDateString()) return "Aujourd'hui";
    const y = new Date(t); y.setDate(t.getDate() - 1);
    if (d.toDateString() === y.toDateString()) return 'Hier';
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  }
  function linkify(s) { return s.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline">$1</a>'); }

  return () => { unsub.forEach((f) => f && f()); ctx.shell.setActiveConversation(null); content.style.padding = ''; };
}
